import { useEffect, useRef, useState } from "react";

/**
 * Stellt fremdes Mail-HTML in einem abgeschotteten Rahmen dar.
 *
 * Newsletter bringen eigene Stylesheets mit und sind in verschachtelten
 * Tabellen gesetzt. Direkt ins Dokument der App gehängt, geschieht beides
 * gleichzeitig: Die App-Regeln (max-width, border-collapse, border-radius,
 * dunkler Grund) zerlegen das Layout der Mail, und die Regeln der Mail könnten
 * die Oberfläche umgestalten. Ein eigener Rahmen trennt beide Seiten sauber.
 *
 * Er läuft ohne Skripte: kein `allow-scripts`, und zusätzlich verbietet eine
 * Inhaltsrichtlinie im Dokument selbst jede Ressource, die nicht ausdrücklich
 * erlaubt ist. `allow-same-origin` ist damit ungefährlich — es gibt nichts, was
 * davon Gebrauch machen könnte — und erlaubt uns, die Höhe abzumessen.
 */
export function MailRahmen({ html, bilderFrei }: { html: string; bilderFrei: boolean }) {
  const rahmen = useRef<HTMLIFrameElement | null>(null);
  const [hoehe, setHoehe] = useState(320);

  // Externe Bilder erst nach ausdrücklicher Freigabe. Die Adresse steckt in
  // data-extern; ohne Freigabe wandert sie gar nicht erst in src.
  // Nur das Attribut tauschen: Alles, was danach im Tag steht (width, height,
  // style, alt), muss erhalten bleiben — sonst zerfällt das Layout der Mail.
  const inhalt = bilderFrei
    ? html.replace(/(<img\b[^>]*?)\sdata-extern="([^"]+)"(\s+data-blockiert="1")?/gi, (_m, anfang: string, url: string) =>
        `${anfang} src="${url}"`)
    : html;

  const richtlinie = bilderFrei
    ? "default-src 'none'; style-src 'unsafe-inline'; img-src * data: blob:; font-src data:;"
    : "default-src 'none'; style-src 'unsafe-inline'; img-src 'self' data:; font-src data:;";

  const dokument = `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${richtlinie}">
<base target="_blank">
<style>
  /* Mail-HTML rechnet mit weißem Grund und dunkler Schrift — unabhängig davon,
     welches Design die App gerade trägt. */
  :root { color-scheme: light; }
  html, body { margin: 0; padding: 0; background: #fff; color: #1a1a1a; }
  body { font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; }
  img { border: 0; }
  /* Nur die Breite bändigen. Alles Weitere bleibt der Mail überlassen. */
  img, table { max-width: 100%; }
  table { table-layout: auto; }
  /* Geparkte externe Bilder als ruhiger Platzhalter statt als kaputtes Symbol. */
  img[data-blockiert] { background: #f3f3f5; outline: 1px dashed #d6d6dc; outline-offset: -1px; min-width: 16px; min-height: 16px; }
</style></head><body>${inhalt}</body></html>`;

  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;

    const messen = () => {
      const d = el.contentDocument;
      if (!d?.body) return;
      // scrollHeight beider Elemente: Je nach Mail trägt mal das eine, mal das
      // andere die volle Höhe.
      const h = Math.max(d.body.scrollHeight, d.documentElement.scrollHeight);
      if (h > 0) setHoehe(h);
    };

    // Bilder kommen nach dem load-Ereignis nach und verschieben die Höhe —
    // ein ResizeObserver im Rahmen folgt jeder Änderung.
    let beobachter: ResizeObserver | null = null;
    const geladen = () => {
      messen();
      const d = el.contentDocument;
      if (d?.body && "ResizeObserver" in window) {
        beobachter?.disconnect();
        beobachter = new ResizeObserver(messen);
        beobachter.observe(d.body);
      }
    };
    el.addEventListener("load", geladen);
    // Rückfallebene für Browser ohne ResizeObserver im Rahmen.
    const nachmessen = window.setInterval(messen, 400);
    const stopp = window.setTimeout(() => window.clearInterval(nachmessen), 4000);
    return () => {
      el.removeEventListener("load", geladen);
      beobachter?.disconnect();
      window.clearInterval(nachmessen);
      window.clearTimeout(stopp);
    };
  }, [dokument]);

  return (
    <iframe
      ref={rahmen}
      className="mail-rahmen"
      title="Inhalt der Nachricht"
      srcDoc={dokument}
      style={{ height: hoehe }}
      // Ohne allow-scripts. allow-popups, damit Links im neuen Tab aufgehen.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
}
